// per-device Snooze（一鍵暫停告警，APP#19）— 客戶端側「靜音 X 分鐘」。
//
// 範圍：**只抑制本機顯示**（門鈴來電卡 / 事件未讀紅點 bump）。事件本體仍正常
// 入時間軸 / 入庫（只是靜默呈現）；後端推播仍會送達——伺服端推播閘（依 snooze
// 窗抑制 push）是 Scott 的 follow-up，本檔不負責。
//
// 設計沿用 readEvents（APP!92 per-item 已讀）的風格：
//   - per-device 截止時戳（epoch ms）存 localStorage（survive refresh）；
//   - 到期自動失效（isSnoozed 比 Date.now()，不需背景計時器）；
//   - 登出（signaling state 'closed'）連同 unread/read 一起清空——共用瀏覽器
//     避免跨帳號 snooze 狀態外洩（與 readEvents.clear() 同一清理點，見
//     liveEvents.ts wireRegistry）。
// 純函式、無 React 依賴，好測（含假時間）。

const SNOOZE_LS_KEY = 'ls.devices.snooze.v1';

/** 自訂分鐘的夾制範圍（防呆 + 防無意義巨值）。 */
export const SNOOZE_MIN_MINUTES = 1;
export const SNOOZE_MAX_MINUTES = 24 * 60; // 24 小時

/** 預設快選（分鐘）：30 分 / 1 時 / 2 時 / 4 時。 */
export const SNOOZE_PRESETS_MINUTES = [30, 60, 120, 240] as const;

export interface ActiveSnooze {
  deviceId: string;
  /** 截止時戳（epoch ms）。 */
  until: number;
}

type Listener = () => void;

/** deviceId → 截止時戳（epoch ms）。 */
let snoozes: Map<string, number> = load();
let version = 0;
const subs = new Set<Listener>();

function load(): Map<string, number> {
  try {
    const raw = globalThis.localStorage?.getItem(SNOOZE_LS_KEY);
    if (!raw) return new Map();
    const obj: unknown = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return new Map();
    const m = new Map<string, number>();
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (typeof k === 'string' && k && typeof v === 'number' && Number.isFinite(v)) m.set(k, v);
    }
    return m;
  } catch {
    return new Map(); // 壞 JSON / SSR → 空
  }
}

function persist(): void {
  try {
    const obj: Record<string, number> = {};
    for (const [k, v] of snoozes) obj[k] = v;
    globalThis.localStorage?.setItem(SNOOZE_LS_KEY, JSON.stringify(obj));
  } catch {
    /* quota / SSR — 退化為記憶體內狀態 */
  }
}

function notify(): void {
  version += 1;
  for (const fn of subs) fn();
}

/** 移除所有已過期項（讀路徑順手清；回傳是否有變動）。 */
function pruneExpired(now: number): boolean {
  let changed = false;
  for (const [id, until] of snoozes) {
    if (until <= now) {
      snoozes.delete(id);
      changed = true;
    }
  }
  return changed;
}

/** 設定（或覆蓋）某裝置 snooze X 分鐘，自當下起算。回傳截止時戳。 */
export function setSnooze(deviceId: string, minutes: number, now: number = Date.now()): number {
  if (!deviceId) return 0;
  const m = Math.min(SNOOZE_MAX_MINUTES, Math.max(SNOOZE_MIN_MINUTES, Math.round(minutes)));
  const until = now + m * 60_000;
  snoozes.set(deviceId, until);
  persist();
  notify();
  return until;
}

/** 取截止時戳（epoch ms）；未 snooze 或已過期回 null（過期順手清除）。 */
export function getSnoozeUntil(deviceId: string, now: number = Date.now()): number | null {
  if (!deviceId) return null;
  const until = snoozes.get(deviceId);
  if (until == null) return null;
  if (until <= now) {
    snoozes.delete(deviceId);
    persist();
    notify();
    return null;
  }
  return until;
}

/** 該裝置目前是否在 snooze 窗內（到期自動失效）。 */
export function isSnoozed(deviceId: string, now: number = Date.now()): boolean {
  return getSnoozeUntil(deviceId, now) != null;
}

/** 提前恢復（清除單一裝置 snooze）。 */
export function clearSnooze(deviceId: string): void {
  if (!deviceId || !snoozes.has(deviceId)) return;
  snoozes.delete(deviceId);
  persist();
  notify();
}

/** 目前仍生效（未過期）的所有 snooze，依截止時間近→遠排序。 */
export function activeSnoozes(now: number = Date.now()): ActiveSnooze[] {
  if (pruneExpired(now)) {
    persist();
    notify();
  }
  return [...snoozes.entries()]
    .map(([deviceId, until]) => ({ deviceId, until }))
    .sort((a, b) => a.until - b.until);
}

/** 全清（登出時呼叫；避免共用瀏覽器跨帳號外洩）。 */
export function clearAllSnoozes(): void {
  if (snoozes.size === 0) {
    try {
      globalThis.localStorage?.removeItem(SNOOZE_LS_KEY);
    } catch {
      /* SSR */
    }
    return;
  }
  snoozes = new Map();
  try {
    globalThis.localStorage?.removeItem(SNOOZE_LS_KEY);
  } catch {
    /* SSR */
  }
  notify();
}

/** useSyncExternalStore 快照（每次變更遞增，供 React 重繪）。 */
export function getSnoozeVersion(): number {
  return version;
}

export function subscribeSnooze(fn: Listener): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

/**
 * 剩餘毫秒 → 倒數標籤（"4 小時" 類粗粒度近似；< 1 分顯示「不到 1 分鐘」）。
 * 純格式、不帶 i18n（呼叫端以 t() 包語意字串，這裡只給數值 + 單位 key）。
 * 回傳結構供呼叫端拼 i18n：{ unit: 'minutes'|'hours', value }。
 */
export function snoozeRemaining(until: number, now: number = Date.now()): { unit: 'expired' | 'soon' | 'minutes' | 'hours'; value: number } {
  const ms = until - now;
  if (ms <= 0) return { unit: 'expired', value: 0 };
  const totalMin = ms / 60_000;
  if (totalMin < 1) return { unit: 'soon', value: 0 };
  if (totalMin < 60) return { unit: 'minutes', value: Math.ceil(totalMin) };
  return { unit: 'hours', value: Math.ceil(totalMin / 60) };
}

/** 測試用：重置模組狀態。 */
export function __resetSnoozeForTest(): void {
  snoozes = new Map();
  version = 0;
  subs.clear();
  try {
    globalThis.localStorage?.removeItem(SNOOZE_LS_KEY);
  } catch {
    /* SSR */
  }
}
